import { useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageIcon, Upload, X } from 'lucide-react'
import { api, type MediaItem } from '../lib/api'
import { prepareImage } from '../lib/image'
import { Spinner } from '../components/ui'
import { FieldShell } from './Fields'

type MediaList = { items: MediaItem[]; publicBaseUrl: string }

export function useMedia() {
  return useQuery({ queryKey: ['media'], queryFn: () => api.get<MediaList>('/media') })
}

/** Upload a file from disk (resized in the browser first); resolves to its media key. */
export async function uploadImage(file: File, folder: string): Promise<string> {
  const { blob, name } = await prepareImage(file)
  const res = await api.upload<{ key: string }>(`/media?folder=${folder}&name=${encodeURIComponent(name)}`, blob)
  return res.key
}

export default function MediaField({ name, label, folder, help }: { name: string; label: string; folder: string; help?: string }) {
  const media = useMedia()
  const qc = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const key = field.value as string
        const url = key && media.data ? `${media.data.publicBaseUrl}/${key}` : null
        return (
          <FieldShell name={name} label={label} help={help}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="grid h-20 w-28 place-items-center overflow-hidden rounded-lg border border-line bg-surface-2">
                {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-faint" />}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()}>
                  {busy ? <Spinner /> : <Upload className="h-3.5 w-3.5" />} Upload
                </button>
                <button type="button" className="btn" onClick={() => setPicking(!picking)}>From library</button>
                {key && (
                  <button type="button" className="btn" onClick={() => field.onChange('')}>
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file) return
                  setBusy(true)
                  setError(null)
                  try {
                    field.onChange(await uploadImage(file, folder))
                    await qc.invalidateQueries({ queryKey: ['media'] })
                  } catch (err) {
                    setError((err as Error).message)
                  } finally {
                    setBusy(false)
                  }
                }}
              />
            </div>
            {key && <p className="mt-1 font-mono text-[11px] text-faint">{key}</p>}
            {error && <p className="mt-1 text-xs text-danger">{error}</p>}
            {picking && media.data && (
              <div className="mt-3 grid max-h-72 grid-cols-4 gap-2 overflow-y-auto rounded-lg border border-line p-2 sm:grid-cols-6">
                {media.data.items.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    title={m.key}
                    className={`aspect-square overflow-hidden rounded border ${m.key === key ? 'border-accent ring-2 ring-accent/30' : 'border-line'}`}
                    onClick={() => { field.onChange(m.key); setPicking(false) }}
                  >
                    <img src={`${media.data.publicBaseUrl}/${m.key}`} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </FieldShell>
        )
      }}
    />
  )
}
